import { useState, useEffect } from 'react'
import { markOauthRedirectInitiated } from '@/lib/analytics'

const API_BASE = import.meta.env.VITE_API_URL || ''

const funFacts = [
  "Over 500 hours of video are uploaded to YouTube every single minute.",
  "YouTube has over 2.7 billion monthly active users worldwide.",
  "Over 1 billion hours of video are watched on YouTube every single day.",
  "63% of YouTube watch time happens on mobile devices.",
  "YouTube Shorts now get over 200 billion views every single day.",
  "YouTube generated more than $60 billion in revenue in 2025, more than Netflix.",
  "YouTube is on track to surpass 30 billion videos in 2026.",
  "Over 1 billion people use YouTube every single day.",
  "MrBeast has over 485 million subscribers, more than the population of the US.",
  "The first ever YouTube video was uploaded on April 23, 2005. It was 18 seconds long.",
  "YouTube is available in over 100 countries and 80 languages.",
  "Over 125 million people subscribe to YouTube Premium and Music.",
  "India has the most YouTube users at over 518 million, followed by the US at 253 million.",
]

// Auth-error banner copy. Keys match `?error=<key>` values the backend sets
// when redirecting back to the landing page. Anything not in this map falls
// back to a generic "sign-in failed" message so unrecognised codes don't
// silently disappear.
const AUTH_ERROR_COPY: Record<string, { title: string; body: string }> = {
  missing_youtube_scope: {
    title: 'Rundown needs YouTube read access',
    body: 'When you sign in, leave the “View your YouTube account” checkbox ticked on Google’s consent screen — otherwise we can’t find your subscribed channels.',
  },
  auth_failed: {
    title: 'Sign-in failed',
    body: 'Something went wrong while signing in with Google. Please try again.',
  },
  no_code: {
    title: 'Sign-in cancelled',
    body: 'The Google sign-in was cancelled before it finished. Try again to continue.',
  },
}

export function LandingPage() {
  const [factIndex, setFactIndex] = useState(() => Math.floor(Math.random() * funFacts.length))
  const [fade, setFade] = useState(true)

  // Parse ?error=<key> set by the OAuth callback. Forcing consent on retry
  // (?force=true) re-shows the per-permission checkbox screen so the user
  // can correct a partial-grant — see backend/src/routes/auth.ts.
  const errorKey = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('error') ?? undefined
    : undefined
  const errorCopy = errorKey ? AUTH_ERROR_COPY[errorKey] : undefined

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false)
      setTimeout(() => {
        setFactIndex(i => (i + 1) % funFacts.length)
        setFade(true)
      }, 400)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  const handleSignIn = () => {
    markOauthRedirectInitiated()
    // If we're recovering from a missing-scope error, force the consent
    // screen so the user sees the YouTube checkbox again. Without `force`,
    // Google may silently re-issue tokens at the previously-granted (and
    // still incomplete) scope set.
    const force = errorKey === 'missing_youtube_scope' ? '?force=true' : ''
    window.location.href = `${API_BASE}/auth/google/start${force}`
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* Left: Sign-in panel */}
        <div className="relative flex flex-col px-6 py-8 sm:px-10 sm:py-10">
          {/* Subtle violet glow behind the sign-in column on small screens */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 lg:hidden"
            style={{
              background: 'radial-gradient(ellipse at top, rgba(124,92,252,0.10), transparent 60%)',
            }}
          />

          {/* Top: brand */}
          <a href="/" className="relative z-10 flex items-center gap-2.5">
            <img src="/logo.svg" alt="Rundown" className="h-8 w-8" />
            <span className="text-xl font-bold tracking-tight">Rundown</span>
          </a>

          {/* Hero + form */}
          <div className="relative z-10 flex flex-1 flex-col justify-center py-12">
            <div className="mx-auto w-full max-w-sm">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                Adaptive knowledge extraction
              </div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
                Sign in to your knowledge library
              </h1>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                One daily email. Every creator you follow, structured. Every idea searchable, forever.
              </p>

              {errorCopy && (
                <div
                  role="alert"
                  className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-left"
                >
                  <p className="text-sm font-semibold text-amber-200">{errorCopy.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-amber-100/85">{errorCopy.body}</p>
                </div>
              )}

              {/* Google sign-in button */}
              <button
                onClick={handleSignIn}
                className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-gray-900 shadow-lg shadow-black/30 ring-1 ring-black/5 transition-all hover:bg-gray-50 hover:shadow-xl active:scale-[0.99]"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Continue with Google
              </button>

              {/* Terms agreement */}
              <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground/70">
                By continuing, you agree to our{' '}
                <a
                  href="https://getrundown.xyz/terms.html"
                  className="text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://getrundown.xyz/privacy.html"
                  className="text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                >
                  Privacy Policy
                </a>
                .
              </p>

              {/* Did you know */}
              <div className="mt-10 rounded-2xl border border-border/60 bg-card/40 p-5">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
                  Did you know?
                </p>
                <p
                  className={`text-sm leading-relaxed text-muted-foreground transition-opacity duration-300 ${
                    fade ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {funFacts[factIndex]}
                </p>
              </div>
            </div>
          </div>

          {/* Legal footer */}
          <div className="relative z-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground/50">
            <a href="https://getrundown.xyz" className="transition-colors hover:text-muted-foreground">Home</a>
            <span>·</span>
            <a href="https://getrundown.xyz/privacy.html" className="transition-colors hover:text-muted-foreground">Privacy</a>
            <span>·</span>
            <a href="https://getrundown.xyz/terms.html" className="transition-colors hover:text-muted-foreground">Terms</a>
            <span>·</span>
            <a href="https://getrundown.xyz/refund.html" className="transition-colors hover:text-muted-foreground">Refund</a>
          </div>
        </div>

        {/* Right: digest preview (desktop only) */}
        <div className="relative hidden overflow-hidden border-l border-border/40 lg:flex lg:items-center lg:justify-center lg:px-12 xl:px-20">
          {/* Branded gradient */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at top right, rgba(124,92,252,0.22), transparent 55%), radial-gradient(ellipse at bottom left, rgba(45,212,191,0.08), transparent 55%)',
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
          />

          {/* Digest mockup card */}
          <div className="relative w-full max-w-md">
            <div
              className="overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl shadow-black/50"
              style={{ animation: 'rundownFloat 6s ease-in-out infinite' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border/60 bg-secondary/40 px-6 py-4">
                <div className="flex items-center gap-2">
                  <img src="/logo.svg" alt="" className="h-5 w-5" />
                  <span className="text-sm font-semibold">Your Rundown · Today</span>
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
                  3 new
                </span>
              </div>

              {/* Entry 1 */}
              <DigestEntry
                avatarInitials="AK"
                avatarColor="violet"
                creator="Andrej Karpathy"
                age="8h ago"
                title="Let's build GPT from scratch"
                keypoint={{
                  time: '14:22',
                  text: '"Attention is the only mechanism that lets the model look at all positions at once."',
                }}
                verdict="you need a ground-up transformer explanation"
              />

              {/* Entry 2 */}
              <DigestEntry
                avatarInitials="MK"
                avatarColor="teal"
                creator="MKBHD"
                age="14h ago"
                title="Galaxy S26 Ultra: 72 Hours Later"
                verdict="you're tracking flagship phone launches this quarter"
              />

              {/* Entry 3 */}
              <DigestEntry
                avatarInitials="3B"
                avatarColor="amber"
                creator="3Blue1Brown"
                age="22h ago"
                title="The beauty of Fourier transforms"
                verdict="you want the intuition behind signal processing"
              />
            </div>

            {/* Caption under card */}
            <p className="mt-6 text-center text-xs text-muted-foreground/70">
              A preview. Your real digest is built from the channels <em className="not-italic text-muted-foreground">you</em> follow.
            </p>
          </div>
        </div>
      </div>

      {/* Keyframes for the gentle float */}
      <style>{`
        @keyframes rundownFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="rundownFloat"] { animation: none !important; }
        }
      `}</style>
    </div>
  )
}

type AvatarColor = 'violet' | 'teal' | 'amber'

const AVATAR_STYLES: Record<AvatarColor, string> = {
  violet: 'bg-primary/20 text-primary',
  teal: 'bg-teal-400/20 text-teal-300',
  amber: 'bg-amber-400/20 text-amber-300',
}

interface DigestEntryProps {
  avatarInitials: string
  avatarColor: AvatarColor
  creator: string
  age: string
  title: string
  keypoint?: { time: string; text: string }
  verdict: string
}

function DigestEntry({ avatarInitials, avatarColor, creator, age, title, keypoint, verdict }: DigestEntryProps) {
  return (
    <div className="border-t border-border/60 px-6 py-5 first:border-t-0">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${AVATAR_STYLES[avatarColor]}`}>
          {avatarInitials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-muted-foreground">{creator} · YouTube · {age}</div>
          <div className="mt-0.5 truncate text-sm font-semibold">{title}</div>
        </div>
      </div>
      {keypoint && (
        <div
          className="mt-3 rounded-r-lg border-l-2 px-4 py-3"
          style={{
            background: 'rgba(124, 92, 252, 0.06)',
            borderColor: 'rgba(124, 92, 252, 0.4)',
          }}
        >
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-primary">Key point @ {keypoint.time} ·</span>{' '}
            {keypoint.text}
          </p>
        </div>
      )}
      <p className="mt-3 text-xs leading-relaxed text-teal-300/90">
        <span className="font-medium not-italic text-muted-foreground">Worth watching if:</span>{' '}
        {verdict}.
      </p>
    </div>
  )
}
